import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ReactSVG } from "react-svg";

import {
   getNotifications,
   markNotificationRead,
} from "../../api/notificationsService";
import {
   formatNotificationTime,
   isNotificationRead,
   notificationBody,
   notificationCreatedAt,
   notificationGroup,
   notificationId,
   notificationTitle,
   notificationVideoMeta,
   videoNotificationLead,
} from "../../utils/notificationsFormat";
import {
   getPushPermission,
   isPushSupported,
   requestPushPermission,
} from "../../utils/pushNotifications";

import bellIcon from "../../assets/svg/mingcute--notification-line.svg";

const PAGE_SIZE = 20;

function mergeById(prev, incoming) {
   const map = new Map();
   for (const item of prev) {
      const id = notificationId(item);
      if (id != null) map.set(String(id), item);
   }
   for (const item of incoming) {
      const id = notificationId(item);
      if (id != null) map.set(String(id), item);
   }
   return Array.from(map.values());
}

function toMs(value) {
   if (!value) return 0;
   const ms = new Date(value).getTime();
   return Number.isFinite(ms) ? ms : 0;
}

function NotificationCard({ item, onOpen, pushPermission, onEnablePush }) {
   const group = notificationGroup(item);
   const read = isNotificationRead(item);
   const title = notificationTitle(item);
   const body = notificationBody(item);
   const videoMeta = notificationVideoMeta(item);
   const time = formatNotificationTime(notificationCreatedAt(item));
   const showPushBtn = isPushSupported() && pushPermission === "default";

   const handleOpenVideo = (event) => {
      event.stopPropagation();
      const href = String(videoMeta?.watchUrl || "").trim();
      if (href) {
         window.open(href, "_blank", "noopener,noreferrer");
      }
      onOpen(item);
   };

   const handleEnablePushClick = (event) => {
      event.stopPropagation();
      onEnablePush?.();
   };

   if (videoMeta) {
      const videoLead = videoNotificationLead(body, videoMeta.title);

      return (
         <div
            className={
               "notificationsPopup__card notificationsPopup__card--video" +
               (read ? "" : " notificationsPopup__card--unread")
            }
            onClick={() => onOpen(item)}
         >
            <p className="notificationsPopup__cardHead">
               <span className="notificationsPopup__cardTitle">{title}</span>
               {time ? (
                  <span className="notificationsPopup__time">{time}</span>
               ) : null}
            </p>

            <div className="notificationsPopup__videoRow">
               <div className="notificationsPopup__videoMedia">
                  <div
                     className="studentVideoLibraryPopup__thumb"
                     style={{
                        backgroundImage: `url(${videoMeta.thumbUrl})`,
                     }}
                  >
                     <p
                        className="studentVideoLibraryPopup__cardTitle"
                        title={videoMeta.title}
                     >
                        {videoMeta.title}
                     </p>
                  </div>
               </div>

               <div className="notificationsPopup__videoCopy">
                  <div className="notificationsPopup__videoCopyMain">
                     <p className="notificationsPopup__cardBody">{videoLead}</p>
                  </div>
                  <div className="notificationsPopup__videoActions">
                     <button
                        type="button"
                        className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--ghost notificationsPopup__videoBtn"
                        onClick={handleOpenVideo}
                     >
                        Deschide
                     </button>
                     {showPushBtn ? (
                        <button
                           type="button"
                           className="studentVideoLibraryPopup__button studentVideoLibraryPopup__button--ghost notificationsPopup__videoBtn notificationsPopup__videoBtn--icon"
                           onClick={handleEnablePushClick}
                           aria-label="Activează notificările pe telefon"
                           title="Activează notificările"
                        >
                           <ReactSVG src={bellIcon} />
                        </button>
                     ) : null}
                  </div>
               </div>
            </div>

            {read ? null : (
               <span className="notificationsPopup__dot" aria-hidden="true" />
            )}
         </div>
      );
   }

   return (
      <button
         type="button"
         className={
            "notificationsPopup__card" +
            (read ? "" : " notificationsPopup__card--unread") +
            ` notificationsPopup__card--${group.toLowerCase()}`
         }
         onClick={() => onOpen(item)}
      >
         <p className="notificationsPopup__cardHead">
            <span className="notificationsPopup__cardTitle">{title}</span>
            {time ? (
               <span className="notificationsPopup__time">{time}</span>
            ) : null}
         </p>
         {body ? (
            <p className="notificationsPopup__cardBody">{body}</p>
         ) : null}
         {read ? null : (
            <span className="notificationsPopup__dot" aria-hidden="true" />
         )}
      </button>
   );
}

export default function NotificationsPopup() {
   const [items, setItems] = useState([]);
   const [page, setPage] = useState(1);
   const [total, setTotal] = useState(0);
   const [loading, setLoading] = useState(true);
   const [loadingMore, setLoadingMore] = useState(false);
   const [error, setError] = useState("");
   const [pushPermission, setPushPermission] = useState(() =>
      getPushPermission(),
   );

   const aliveRef = useRef(true);
   useEffect(() => {
      aliveRef.current = true;
      return () => {
         aliveRef.current = false;
      };
   }, []);

   const load = useCallback(async (nextPage, mode) => {
      if (mode === "more") setLoadingMore(true);
      else setLoading(true);
      setError("");

      try {
         const res = await getNotifications({
            page: nextPage,
            limit: PAGE_SIZE,
            unreadOnly: false,
         });
         if (!aliveRef.current) return;

         setTotal(res.total);
         setPage(nextPage);
         setItems((prev) =>
            mode === "more" ? mergeById(prev, res.items) : res.items,
         );
      } catch (e) {
         if (!aliveRef.current) return;
         setError(e?.message || "Nu s-au putut încărca notificările.");
         if (mode !== "more") setItems([]);
      } finally {
         if (aliveRef.current) {
            setLoading(false);
            setLoadingMore(false);
         }
      }
   }, []);

   useEffect(() => {
      load(1, "reset");
   }, [load]);

   const sortedItems = useMemo(() => {
      return items
         .slice()
         .sort(
            (a, b) =>
               toMs(notificationCreatedAt(b)) - toMs(notificationCreatedAt(a)),
         );
   }, [items]);

   const hasMore = items.length < total;

   const handleOpen = useCallback(async (item) => {
      const id = notificationId(item);
      if (id == null || isNotificationRead(item)) return;

      setItems((prev) =>
         prev.map((n) =>
            String(notificationId(n)) === String(id)
               ? { ...n, isRead: true, read: true }
               : n,
         ),
      );

      try {
         await markNotificationRead(id);
      } catch {
         if (aliveRef.current) {
            setItems((prev) =>
               prev.map((n) =>
                  String(notificationId(n)) === String(id)
                     ? { ...n, isRead: false, read: false }
                     : n,
               ),
            );
         }
      }
   }, []);

   const handleEnablePush = useCallback(async () => {
      const result = await requestPushPermission();
      setPushPermission(result);
   }, []);

   const showPushCta = isPushSupported() && pushPermission === "default";
   const isEmpty = sortedItems.length === 0;

   return (
      <div className="notificationsPopup">
         <div className="notificationsPopup__header">
            <h3 className="notificationsPopup__title">Notificări</h3>
         </div>

         {showPushCta ? (
            <button
               type="button"
               className="notificationsPopup__pushCta"
               onClick={handleEnablePush}
            >
               <ReactSVG
                  className="notificationsPopup__pushIcon"
                  src={bellIcon}
               />
               <span>Activează notificările pe telefon / desktop</span>
            </button>
         ) : null}

         {loading ? (
            <p className="notificationsPopup__state">Se încarcă...</p>
         ) : (
            <>
               {error ? (
                  <p className="notificationsPopup__error">{error}</p>
               ) : null}

               {isEmpty && !error ? (
                  <div className="notificationsPopup__empty">
                     <ReactSVG
                        className="notificationsPopup__emptyIcon"
                        src={bellIcon}
                     />
                     <p className="notificationsPopup__emptyText">
                        Nu ai notificări.
                     </p>
                  </div>
               ) : (
                  <div className="notificationsPopup__content">
                     <div className="notificationsPopup__grid">
                        {sortedItems.map((item, index) => (
                           <NotificationCard
                              key={
                                 notificationId(item) != null
                                    ? `n-${notificationId(item)}`
                                    : `n-${index}`
                              }
                              item={item}
                              onOpen={handleOpen}
                              pushPermission={pushPermission}
                              onEnablePush={handleEnablePush}
                           />
                        ))}
                     </div>

                     {hasMore ? (
                        <button
                           type="button"
                           className="notificationsPopup__more"
                           onClick={() => load(page + 1, "more")}
                           disabled={loadingMore}
                        >
                           {loadingMore ? "Se încarcă..." : "Încarcă mai multe"}
                        </button>
                     ) : null}
                  </div>
               )}
            </>
         )}
      </div>
   );
}
